import { AsyncStatus } from 'lib-shared-legacy-types';
import { handleActions } from 'redux-actions';

import { twilioActions } from '../actions/twilioActions';

const {
  twilioMakeCall,
  twilioMakeCallSucceeded,
  twilioMakeCallFailed,
  twilioMakeInternalCall,
  twilioMakeInternalCallSucceeded,
  twilioMakeInternalCallFailed,
  twilioMakeCallback,
  twilioMakeCallbackSucceeded,
  twilioMakeCallbackFailed,
  twilioConnectCall,
  twilioConnectCallSucceeded,
  twilioConnectCallFailed,
  twilioDisconnectCall,
  twilioDisconnectCallSucceeded,
  twilioDisconnectCallFailed,
  twilioFeedback,
  twilioFeedbackSucceeded,
  twilioFeedbackFailed,
  twilioFeedbackOpenModal,
  twilioFeedbackCloseModal,
} = twilioActions;

export type TwilioState = {
  meta: {
    connectCall: AsyncStatus;
    makeCall: AsyncStatus;
    makeCallback: AsyncStatus;
    internalCall: AsyncStatus;
    disconnect: AsyncStatus;
    feedback: AsyncStatus;
  };
  /** The user being dialed while an internal call is being established. */
  pendingInternalCallUserId: string | null;
  ui: {
    isFeedbackModalOpen: boolean;
  };
};

// Init state for the twilio.
const initialState: TwilioState = {
  meta: {
    connectCall: 'succeeded',
    makeCall: 'succeeded',
    makeCallback: 'succeeded',
    internalCall: 'succeeded',
    disconnect: 'succeeded',
    feedback: 'succeeded',
  },
  pendingInternalCallUserId: null,
  ui: {
    isFeedbackModalOpen: false,
  },
};

// Reducer
export const twilio = handleActions(
  {
    [twilioFeedbackOpenModal.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, ui: { ...state.ui, isFeedbackModalOpen: true } };
    },

    [twilioFeedbackCloseModal.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, ui: { ...state.ui, isFeedbackModalOpen: false } };
    },

    [twilioFeedback.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, feedback: 'loading' } };
    },

    [twilioFeedbackSucceeded.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, feedback: 'succeeded' } };
    },

    [twilioFeedbackFailed.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, feedback: 'failed' } };
    },

    [twilioMakeCall.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCall: 'loading' } };
    },

    [twilioMakeCallSucceeded.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCall: 'succeeded' } };
    },

    [twilioMakeCallFailed.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCall: 'failed' } };
    },

    [twilioMakeCallback.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCallback: 'loading' } };
    },

    [twilioMakeCallbackSucceeded.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCallback: 'succeeded' } };
    },

    [twilioMakeCallbackFailed.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, meta: { ...state.meta, makeCallback: 'failed' } };
    },

    [twilioMakeInternalCall.toString()]: (
      state: TwilioState,
      action: { payload: { userId?: string } },
    ): TwilioState => {
      return {
        ...state,
        meta: { ...state.meta, internalCall: 'loading' },
        pendingInternalCallUserId: action.payload?.userId ?? null,
      };
    },

    [twilioMakeInternalCallSucceeded.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return {
        ...state,
        meta: { ...state.meta, internalCall: 'succeeded' },
        pendingInternalCallUserId: null,
      };
    },

    [twilioMakeInternalCallFailed.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return {
        ...state,
        meta: { ...state.meta, internalCall: 'failed' },
        pendingInternalCallUserId: null,
      };
    },

    [twilioConnectCall.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, connectCall: 'loading' } };
    },

    [twilioConnectCallSucceeded.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, meta: { ...state.meta, connectCall: 'succeeded' } };
    },

    [twilioConnectCallFailed.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, connectCall: 'failed' } };
    },

    [twilioDisconnectCall.toString()]: (state: TwilioState): TwilioState => {
      return { ...state, meta: { ...state.meta, disconnect: 'loading' } };
    },

    [twilioDisconnectCallSucceeded.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, meta: { ...state.meta, disconnect: 'succeeded' } };
    },

    [twilioDisconnectCallFailed.toString()]: (
      state: TwilioState,
    ): TwilioState => {
      return { ...state, meta: { ...state.meta, disconnect: 'failed' } };
    },
  },
  initialState,
);
